import { escapeHtml } from './escapeHtml.js';

/**
 * Confirmation dialog for unsaved changes, with three options:
 * - "Guarda i surt" (save + exit)
 * - "Surt sense guardar" (discard + exit)
 * - "Cancel·la" (stay in edit mode)
 *
 * Fully self-contained (appends an overlay + dialog to `document.body`);
 * not tied to any particular view, so it lives here rather than inside
 * GraphVisualizer (where it originated, back when GraphVisualizer was the
 * app's only view). `recipeTitle`/`isMobile` are passed in rather than
 * read from view state directly.
 */
export function showUnsavedChangesDialog(options: {
  recipeTitle: string;
  isMobile: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
}): void {
  const { recipeTitle, isMobile, onSave, onDiscard, onCancel } = options;

  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  `;

  const dialog = document.createElement('div');
  dialog.style.cssText = `
    background: #1e1e2e;
    border: 1px solid var(--accent-alpha-30);
    border-radius: 12px;
    padding: 24px;
    max-width: 450px;
    width: 90%;
    font-family: 'Courier New', Consolas, 'Liberation Mono', monospace;
  `;

  dialog.innerHTML = `
    <h3 style="color:#e0e0e0; font-size:16px; margin-bottom:12px; font-family:inherit;">Canvis sense guardar</h3>
    <p style="color:#a0a0a0; font-size:13px; margin-bottom:20px; line-height:1.5; font-family:inherit;">
      Teniu canvis sense guardar a "${escapeHtml(recipeTitle)}". Què voleu fer?
    </p>
    <div style="display:flex; gap:8px; justify-content:flex-end; flex-wrap:wrap;">
      <button class="dialog-btn-cancel" style="
        padding:8px 16px; border-radius:6px; border:1px solid #555;
        background:transparent; color:#a0a0a0; cursor:pointer; font-family:inherit; font-size:13px;
      ">Cancel·la</button>
      <button class="dialog-btn-discard" style="
        padding:8px 16px; border-radius:6px; border:1px solid #ef4444;
        background:transparent; color:#ef4444; cursor:pointer; font-family:inherit; font-size:13px;
      ">Surt sense guardar</button>
      <button class="dialog-btn-save" style="
        padding:8px 16px; border-radius:6px; border:none;
        background:var(--accent); color:#0d1117; cursor:pointer; font-family:inherit; font-size:13px; font-weight:bold;
      ">Guarda i surt</button>
    </div>
  `;

  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  dialog.querySelector('.dialog-btn-cancel')?.addEventListener('click', () => {
    overlay.remove();
    onCancel();
  });
  dialog.querySelector('.dialog-btn-discard')?.addEventListener('click', () => {
    overlay.remove();
    onDiscard();
  });
  dialog.querySelector('.dialog-btn-save')?.addEventListener('click', () => {
    overlay.remove();
    onSave();
  });

  // On mobile, make the dialog full-screen (see §8.9)
  if (isMobile) {
    dialog.style.cssText += `
      max-width: 100vw;
      width: 100vw;
      height: 100vh;
      border-radius: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
    `;
    // Stack buttons vertically on mobile
    const btnContainer = dialog.querySelector('div:last-child') as HTMLElement;
    if (btnContainer) {
      btnContainer.style.flexDirection = 'column';
      (btnContainer.querySelectorAll('button') as NodeListOf<HTMLElement>).forEach(b => {
        b.style.width = '100%';
        b.style.textAlign = 'center';
      });
    }
  }

  // Close on overlay click (acts as cancel)
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      overlay.remove();
      onCancel();
    }
  });
}
